import { ColumnDef } from "@tanstack/react-table";
import { AxiosError } from "axios";
import React, { useEffect, useState, useRef } from "react";

import DataTable2 from "shared/DataTable2";
import { useServerPaginatedList } from "../../shared/useServerPaginatedList";
import {
  ServerDeletedFilterValue,
  ServerStatusFilterValue,
} from "../../shared/ServerListFilters";
import ServerDeletedPills from "../../shared/ServerDeletedPills";
import { Button } from "../../components/ui/button";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { ScrollArea } from "../../components/ui/scroll-area";
import { Input } from "../../components/ui/input";
import ServerStatusPills from "../../shared/ServerStatusPills";
import { Label } from "../../components/ui/Label";
import { Toaster } from "../../components/ui/toaster";
import { useToast } from "../../hooks/use-toast";
import { SpecialtyAxiosInstance } from "../../Network/SpecialtyAxiosInstance";
import { LoginAxiosInstance } from "../../Network/LoginAxiosInstance";
import FileUpload from "Pages/uploadFile/Specialty/Specialty";
import useQaAutomationIds from "../../utils/useQaAutomationIds";

// ─── Interfaces ──────────────────────────────────────────────────────────────

export interface SpecialtyResult {
  id: number;
  name: string;
  speciality_code?: string;
  is_active: boolean | string;
  is_deleted?: boolean;
  deleted_by?: any;
  status?: string;
  created_by?: any;
  createdat?: string;
  updated_by?: any[];
  updatedat?: string;
}

export interface SpecialtyData {
  result: SpecialtyResult[];
}

// ─── Role Helper (Step 1) ─────────────────────────────────────────────────────

const getUserRole = (): string => {
  try {
    const token = localStorage.getItem("token") || "";
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload.userType || "";
  } catch { return ""; }
};

// ─── Component ───────────────────────────────────────────────────────────────

const Specialty: React.FC = () => {
  useQaAutomationIds("specialty");
  const [items, setItems] = useState<SpecialtyData>({ result: [] });
  const [name, setName] = useState<string>("");
  const [specialtyCode, setSpecialtyCode] = useState<string>("");
  const [isActive, setIsActive] = useState<boolean>(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editIndex, setEditIndex] = useState<null | number>(null);
  const [filterName, setFilterName] = useState("");
  const [filterStatus, setFilterStatus] = useState<ServerStatusFilterValue>("");
  const [filterDeleted, setFilterDeleted] = useState<ServerDeletedFilterValue>("false");
  const [nameError, setNameError] = useState<string>("");
  const [codeError, setCodeError] = useState<string>("");
  const [selectedItem, setSelectedItem] = useState<SpecialtyResult | null>(null);
  const [usersMap, setUsersMap] = useState<Record<number, string>>({});
  const dialogRef = useRef<HTMLDialogElement | null>(null);
  const [showAllUpdates, setShowAllUpdates] = useState(false);

  // ─── Step 1: Role + canSeeAdminActions ────────────────────────────────────
  const userRole = getUserRole();
  const canManageDeleted = userRole === "system_admin" || userRole === "super_super_admin";

  // ─── Step 4: Force Dialog state ───────────────────────────────────────────
  const [forceDialogOpen, setForceDialogOpen] = useState(false);
  const [forceAction, setForceAction] = useState<"softDelete" | "hardDelete" | "status" | null>(null);
  const [forceId, setForceId] = useState<string | number | null>(null);
  const [forceError, setForceError] = useState<string>("");

  const token = localStorage.getItem("token") || "";
  const { toast } = useToast();

  // ─── Normalize ─────────────────────────────────────────────────────────────

  const normalizeData = (data: SpecialtyResult[]): SpecialtyResult[] =>
    data.map((item) => ({
      ...item,
      status:
        item.is_active === true || item.is_active === "true" || item.is_active === "Active"
          ? "Active"
          : "Inactive",
    }));

  // ─── Users Map ─────────────────────────────────────────────────────────────

  const fetchUsers = async () => {
    try {
      const res = await LoginAxiosInstance.get("/");
      const raw: any[] = res.data?.users ?? res.data?.user ?? res.data ?? [];
      const map: Record<number, string> = {};
      raw.forEach((u: any) => {
        if (u.id != null) map[Number(u.id)] = u.name ?? u.username ?? String(u.id);
      });
      setUsersMap(map);
    } catch {}
  };

  const resolveUserName = (val: any): string => {
    if (val == null) return "—";
    if (typeof val === "object") return val.name ?? val.username ?? "—";
    if (typeof val === "string" && isNaN(Number(val))) return val;
    return usersMap[Number(val)] ?? String(val);
  };

  // ─── Validation ────────────────────────────────────────────────────────────

const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setName(value);
    if (!value.trim()) { setNameError("Name is required"); return; }
    if (value.trim().length < 2) { setNameError("Name must be at least 2 characters"); return; }
    const nameRegex = /^[A-Za-z\s\-\/()*+]+$/;
    if (!nameRegex.test(value)) { setNameError("Only letters, spaces, hyphens, slashes, and parentheses are allowed"); return; }
    setNameError("");
  };

  const existingCodes = items.result
    .filter((item) => !item.is_deleted)
    .map((item) => item.speciality_code?.toLowerCase());

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSpecialtyCode(value);
    if (!value.trim()) { setCodeError(""); return; }
    const alphanumericRegex = /^[a-zA-Z0-9_\-]+$/;
    if (!alphanumericRegex.test(value.trim())) {
      setCodeError("Code must contain only letters, numbers, hyphens, or underscores");
      return;
    }
    if (editIndex === null && existingCodes.includes(value.trim().toLowerCase())) {
      setCodeError("This code already exists");
      return;
    }
    setCodeError("");
  };

  // ─── CRUD ──────────────────────────────────────────────────────────────────

  useEffect(() => {
    fetchUsers();
  }, []);

  const clear = () => {
    setName("");
    setSpecialtyCode("");
    setIsActive(true);
    setNameError("");
    setCodeError("");
  };

  // ─── Step 2: fetchItems — أحدث أول + محذوفين آخر ─────────────────────────
  const {
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    totalPages,
    totalCount,
    allowedPageSizes,
    isTableLoading,
    manualPagination,
    fetchItems,
  } = useServerPaginatedList<SpecialtyResult, {
    name: string;
    active: ServerStatusFilterValue;
    deleted: ServerDeletedFilterValue;
  }>({
    filters: { name: filterName, active: filterStatus, deleted: filterDeleted },
    fetchPage: async ({ page, limit, filters }) => {
      const response = await SpecialtyAxiosInstance.get("getSpecialty", {
        params: {
          page,
          limit,
          name: filters.name.trim() || undefined,
          active: filters.active || undefined,
          is_deleted: filters.deleted,
        },
      });
      return {
        data: {
          ...response.data,
          result: response.data?.result ?? response.data?.activities ?? [],
        },
      };
    },
    setRows: (rows) => setItems({ result: rows }),
    normalizeRow: (item: any) => ({
      ...item,
      status:
        item.is_active === true || item.is_active === "true" || item.is_active === "Active"
          ? "Active"
          : "Inactive",
    }),
    sortRows: (rows) =>
      [...rows]
        .sort((a: any, b: any) => Number(b.id) - Number(a.id))
        .sort((a: any, b: any) =>
          a.is_deleted === b.is_deleted ? 0 : a.is_deleted ? 1 : -1
        ),
    onError: (error) => {
      console.error("Error fetching items:", error);
      toast({ title: "Error", description: "Failed to fetch specialties.", variant: "destructive" });
    },
  });

  // ─── Step 5: Toggle Status — مع force support ─────────────────────────────
  const toggleStatus = async (item: SpecialtyResult, force = false): Promise<void> => {
    if (item.is_deleted) return;
    try {
      const currentStatus = item.status === "Active" || item.is_active === true;
      const url = force ? "updateSpecialty?force=true" : "updateSpecialty";
      const payload = {
        data: { name: item.name, speciality_code: item.speciality_code, is_active: !currentStatus, id: item.id },
        token,
      };
      const response = await SpecialtyAxiosInstance.put(url, payload);
      if (response.data.success) {
        await fetchItems();
        setForceDialogOpen(false);
        setForceError("");
        toast({ title: "Success", description: `Status changed to ${!currentStatus ? "Active" : "Inactive"}.` });
      } else {
        setForceId(item.id);
        setForceAction("status");
        setForceError(response.data.error || "This specialty is used elsewhere.");
        setForceDialogOpen(true);
      }
    } catch (error: any) {
      const errMsg = error.response?.data?.error || "An unexpected error occurred.";
      if (error.response?.status === 409 || errMsg.toLowerCase().includes("used")) {
        setForceId(item.id);
        setForceAction("status");
        setForceError(errMsg);
        setForceDialogOpen(true);
      } else {
        toast({ title: "Error", description: errMsg, variant: "destructive" });
      }
    }
  };

  // ─── Step 10: addItem ─────────────────────────────────────────────────────
  const addItem = async (): Promise<void> => {
    setNameError("");
    setCodeError("");
    if (!name.trim()) {
      setNameError("Name is required");
      toast({ title: "Error", description: "Specialty name is required.", variant: "destructive" });
      return;
    }
    if (name.trim().length < 2) { setNameError("Name must be at least 2 characters"); return; }
    if (nameError || codeError) return;
    try {
      const payload = { data: [{ name, speciality_code: specialtyCode, is_active: isActive }], token };
      const response = await SpecialtyAxiosInstance.post("addNewSpecialty", payload);
      if (response.data.success) {
        await fetchItems();
        setCurrentPage(0);
        clear();
        setIsDialogOpen(false);
        toast({ title: "Success", description: "New specialty Added Successfully." });
      } else {
        toast({ title: "Error", description: response.data.error || "Failed to add specialty.", variant: "destructive" });
      }
    } catch (error: AxiosError | any) {
      toast({ title: "Error", description: error.response?.data?.error || "An unexpected error occurred.", variant: "destructive" });
    }
  };

  const handleEdit = (item: SpecialtyResult) => {
    setNameError("");
    setCodeError("");
    setName(item.name);
    setSpecialtyCode(item.speciality_code || "");
    setIsActive(item.status === "Active" || item.is_active === true);
    setEditIndex(item.id);
  };

  const handleEditClick = (item: SpecialtyResult) => { handleEdit(item); setIsDialogOpen(true); };
  const handleAddClick = () => { setEditIndex(null); clear(); setIsDialogOpen(true); };

  // ─── Step 10: updateItem ─────────────────────────────────────────────────
  const updateItem = async (id: string | number): Promise<void> => {
    if (!name.trim()) { toast({ title: "Error", description: "Specialty name is required.", variant: "destructive" }); return; }
    if (name.trim().length < 2) { toast({ title: "Error", description: "Name must be at least 2 characters.", variant: "destructive" }); return; }
    if (nameError || codeError) return;
    try {
      const payload = { data: { name, speciality_code: specialtyCode, is_active: isActive, id }, token };
      const response = await SpecialtyAxiosInstance.put("updateSpecialty", payload);
      if (response.data.success) {
        await fetchItems();
        setCurrentPage(0);
        clear();
        setIsDialogOpen(false);
        toast({ title: "Success", description: "Specialty updated successfully." });
      }
    } catch (error: AxiosError | any) {
      toast({ title: "Error", description: error.response?.data?.error || "An unexpected error occurred.", variant: "destructive" });
    }
  };

  // ─── Step 3: removeItem → softDelete ─────────────────────────────────────
  const removeItem = async (id: string | number): Promise<void> => {
    try {
      const response = await SpecialtyAxiosInstance.delete(`softDeleteSpecialty/${id}`, { data: { token } });
      if (response.data.success) {
        await fetchItems();
        toast({ title: "Success", description: "Specialty Deleted Successfully." });
      } else {
        setForceId(id);
        setForceAction("softDelete");
        setForceError(response.data.error || "This specialty is used elsewhere.");
        setForceDialogOpen(true);
      }
    } catch (error: any) {
      const errMsg = error.response?.data?.error || "Failed to delete specialty";
      if (error.response?.status === 409 || errMsg.toLowerCase().includes("used")) {
        setForceId(id); setForceAction("softDelete"); setForceError(errMsg); setForceDialogOpen(true);
      } else {
        toast({ title: "Error", description: errMsg, variant: "destructive" });
      }
    }
  };

  // ─── Step 3: restoreItem ─────────────────────────────────────────────────
  const restoreItem = async (id: string | number): Promise<void> => {
    try {
      const response = await SpecialtyAxiosInstance.patch(`restoreSpecialty/${id}`, { token });
      if (response.data.success) {
        await fetchItems();
        toast({ title: "Success", description: "Specialty restored successfully." });
      } else {
        toast({ title: "Error", description: response.data.error || "Failed to restore specialty", variant: "destructive" });
      }
    } catch (error: any) {
      toast({ title: "Error", description: error.response?.data?.error || "Failed to restore specialty", variant: "destructive" });
    }
  };

  // ─── Step 3: permanentDeleteItem → hardDelete ────────────────────────────
  const permanentDeleteItem = async (id: string | number, force = false): Promise<void> => {
    try {
      const url = force ? `deleteSpecialty/${id}?force=true` : `deleteSpecialty/${id}`;
      const response = await SpecialtyAxiosInstance.delete(url, { data: { token } });
      if (response.data.success) {
        await fetchItems();
        setForceDialogOpen(false); setForceId(null); setForceError(""); setForceAction(null);
        toast({ title: "Success", description: "Specialty Permanently Deleted." });
      } else {
        setForceAction("hardDelete");
        setForceError(response.data.error || "This specialty is used elsewhere.");
      }
    } catch (error: any) {
      setForceAction("hardDelete");
      setForceError(error.response?.data?.error || "Failed to delete specialty");
    }
  };

  // ─── Step 3: forceSoftDelete ─────────────────────────────────────────────
  const forceSoftDelete = async (id: string | number): Promise<void> => {
    try {
      const response = await SpecialtyAxiosInstance.delete(`softDeleteSpecialty/${id}?force=true`, { data: { token } });
      if (response.data.success) {
        await fetchItems();
        setForceDialogOpen(false); setForceId(null); setForceError(""); setForceAction(null);
        toast({ title: "Success", description: "Specialty force Deleted Successfully." });
      } else {
        setForceError(response.data.error || "Failed to force delete specialty");
      }
    } catch (error: any) {
      setForceError(error.response?.data?.error || "Failed to force delete specialty");
    }
  };

  const handleDownload = (item: SpecialtyResult) => {
    const rows = ["name;speciality_code;is_active"];
    rows.push([item.name || "", item.speciality_code || "", item.is_active ? "TRUE" : "FALSE"].join(";"));
    const csvContent = rows.join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Specialty${item.id}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleOpenDialog = (item: SpecialtyResult) => { setSelectedItem(item); dialogRef.current?.showModal(); };
const handleCloseDialog = () => { setSelectedItem(null); setShowAllUpdates(false); };  
// ─── Step 6: Columns ──────────────────────────────────────────────────────

  const columns: ColumnDef<SpecialtyResult>[] = [
    {
      accessorKey: "id",
      header: ({ column }) => (
        <Button variant="ghost" className="w-full text-center" onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
          ID{column.getIsSorted() === "asc" ? " ↑" : column.getIsSorted() === "desc" ? " ↓" : ""}
        </Button>
      ),
    cell: ({ row }) => (
  <div className="text-center w-full font-medium">
    {row.index + 1}
  </div>
),
    },
    {
      accessorKey: "name",
      header: ({ column }) => (
        <Button variant="ghost" className="w-full text-center" onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
          Specialty Name{column.getIsSorted() === "asc" ? " ↑" : column.getIsSorted() === "desc" ? " ↓" : ""}
        </Button>
      ),
      cell: ({ row }) => {
        const isDeleted = row.original.is_deleted;
        const value = String(row.getValue("name") ?? "");
        const searchTerm = filterName.toLowerCase().trim();
        const isMatching =
          searchTerm.length >= 2 &&
          value.toLowerCase().includes(searchTerm);

        return (
          <div className="flex w-full justify-center py-3">
            <span
              className={`inline-flex items-center rounded-md border px-2 py-1 text-sm ${
                isDeleted
                  ? "line-through text-gray-400 border-transparent"
                  : isMatching
                    ? "bg-yellow-200 text-yellow-900 border-yellow-400 font-semibold"
                    : "border-transparent"
              }`}
            >
              {value}
            </span>
          </div>
        );
      },
    },
    {
      accessorKey: "speciality_code",
      header: ({ column }) => (
        <Button variant="ghost" className="w-full text-center" onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
          Specialty Code{column.getIsSorted() === "asc" ? " ↑" : column.getIsSorted() === "desc" ? " ↓" : ""}
        </Button>
      ),
      cell: ({ row }) => {
        const code = String(row.getValue("speciality_code") ?? "");
        const searchTerm = filterName.toLowerCase().trim();
        const isMatching =
          searchTerm.length >= 2 &&
          code.toLowerCase().includes(searchTerm);

        return (
          <div className="flex justify-center">
            {code ? (
              <span
                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border uppercase tracking-wide ${
                  isMatching
                    ? "bg-yellow-200 text-yellow-900 border-yellow-400"
                    : "bg-indigo-100 text-indigo-800 border-indigo-200"
                }`}
              >
                {code}
              </span>
            ) : (
              <span className="text-gray-400">—</span>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: "status",
      header: ({ column }) => (
        <Button variant="ghost" className="w-full text-center" onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
          Status{column.getIsSorted() === "asc" ? " ↑" : column.getIsSorted() === "desc" ? " ↓" : ""}
        </Button>
      ),
      cell: ({ row }) => {
        const item = row.original;
        const active = item.status === "Active" || item.is_active === true;
        const isDeleted = item.is_deleted;
        return (
          <div className="flex justify-center">
            {/* ─── Step 6b: disabled لو is_deleted ──────────────────────── */}
            <button
              onClick={() => !isDeleted && toggleStatus(item)}
              disabled={!!isDeleted}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                isDeleted ? "opacity-40 cursor-not-allowed bg-gray-200" : active ? "bg-green-500" : "bg-gray-300"
              }`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                active && !isDeleted ? "translate-x-6" : "translate-x-1"
              }`} />
            </button>
          </div>
        );
      },
    },
  {
  id: "view",
  header: () => <Button variant="ghost" className="w-full text-center">View</Button>,
  cell: ({ row }) => {
    const item = row.original;
    return (
      <div className="w-full flex justify-center items-center gap-2 py-2">
        <button onClick={() => setSelectedItem(item)}  className="text-blue-600 hover:text-blue-800" title="View Details">
          <i className="fa-solid fa-eye text-lg"></i>
        </button>
      </div>
    );
  },
},
  ];

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="h5-container mx-auto px-1 pt-16 pb-4" data-qa-page="specialty">
      <div className="flex justify-between items-center">
        <h5 className="mb-6 flex items-center gap-2 font-bold text-2xl mt-4 text-blue-900">
          <span className="icon-container flex items-center justify-center w-10 h-10 rounded-full bg-blue-900 text-white">
            <i className="fa-solid fa-stethoscope"></i>
          </span>
          Specialty
        </h5>
      </div>

      {/* ─── Step 7: DataTable2 props ─────────────────────────────────────── */}
<DataTable2
        qaIdPrefix="specialty-data-table"
  columns={columns}
  data={items.result}
  searchColumns={[]}
  searchValue={filterName}
  onSearchChange={(value) => {
    setFilterName(value);
    setCurrentPage(0);
  }}
  searchPlaceholder="Search..."
  searchHelpText="Specialty Name or Specialty Code."
  addButtonLabel="Add New Specialty"
  showAddButton={true}
  compactToolbar={true}
toolbarUpload={
  <div
    style={{
      width: 48,
      height: 40,
      position: "relative",
      flexShrink: 0,
    }}
  >
    <div
      style={{
        position: "absolute",
        inset: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <FileUpload fetchItems={fetchItems} />
    </div>
  </div>
}
  onEdit={handleEdit}
  pageIndex={currentPage}
  onPageChange={setCurrentPage}
  manualPagination={manualPagination}
  pageCount={totalPages}
  totalCount={totalCount}
  pageSize={pageSize}
  onPageSizeChange={(nextPageSize) => {
    setPageSize(nextPageSize);
    setCurrentPage(0);
  }}
  allowedPageSizes={allowedPageSizes}
  isLoading={isTableLoading}
  onDelete={(id) => {
    const item = items.result.find((i) => i.id === id);

    if ((item as any)?.is_deleted) {
      setForceId(id);
      setForceAction("hardDelete");
      setForceError("");
      setForceDialogOpen(true);
    } else {
      removeItem(id);
    }
  }}
  onRestore={canManageDeleted ? restoreItem : undefined}
  canHardDelete={canManageDeleted}
  onAdd={handleAddClick}
  onEditClick={handleEditClick}
  onDownload={handleDownload}
  isRowDeleted={(row: any) => row.is_deleted === true}
  toolbarExtras={
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        flexWrap: "wrap",
      }}
    >
      <ServerStatusPills
        value={filterStatus}
        onChange={(value) => {
          setFilterStatus(value);
          setCurrentPage(0);
        }}
      />

      <ServerDeletedPills
        value={filterDeleted}
        onChange={(value) => {
          setFilterDeleted(value);
          setCurrentPage(0);
        }}
      />
    </div>
  }
/>

{selectedItem && (
  <Dialog open={!!selectedItem} onOpenChange={(open) => { if (!open) setSelectedItem(null); }}>
    <DialogContent className="max-w-[780px] max-h-[92vh] flex flex-col p-0 overflow-hidden bg-white border-none rounded-2xl" style={{ boxShadow: "0 25px 60px rgba(0,0,0,0.18), 0 0 0 1px rgba(0,0,0,0.04)" }}>

      {/* Header */}
      <div style={{ background: "linear-gradient(135deg, #0f172a 0%, #1e3a8a 60%, #2563eb 100%)", padding: "24px 28px 20px", flexShrink: 0, position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", top: -40, right: -40, width: 160, height: 160, borderRadius: "50%", background: "rgba(255,255,255,0.04)" }} />
        <div style={{ position: "absolute", bottom: -30, left: -20, width: 100, height: 100, borderRadius: "50%", background: "rgba(255,255,255,0.03)" }} />
        <div style={{ display: "flex", alignItems: "flex-start", gap: 16, position: "relative", zIndex: 1 }}>
          <div style={{ width: 48, height: 48, borderRadius: 14, background: "rgba(255,255,255,0.12)", backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, color: "#fff", flexShrink: 0, border: "1px solid rgba(255,255,255,0.1)" }}>
            <i className="fa-solid fa-stethoscope" />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 2, flexWrap: "wrap" }}>
              <h2 style={{ fontSize: 20, fontWeight: 700, color: "#fff", margin: 0, letterSpacing: "-0.01em" }}>{selectedItem.name}</h2>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 12px", borderRadius: 9999, fontSize: 11, fontWeight: 600, flexShrink: 0, background: (selectedItem.status === "Active" || selectedItem.is_active === true) ? "rgba(34,197,94,0.2)" : "rgba(239,68,68,0.15)", color: (selectedItem.status === "Active" || selectedItem.is_active === true) ? "#86efac" : "#fca5a5", border: `1px solid ${(selectedItem.status === "Active" || selectedItem.is_active === true) ? "rgba(34,197,94,0.25)" : "rgba(239,68,68,0.2)"}` }}>
                <span style={{ width: 6, height: 6, borderRadius: "50%", background: "currentColor" }} />
                {selectedItem.status === "Active" || selectedItem.is_active === true ? "Active" : "Inactive"}
              </span>
              {selectedItem.is_deleted && (
                <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 12px", borderRadius: 9999, fontSize: 11, fontWeight: 600, background: "rgba(239,68,68,0.15)", color: "#fca5a5", border: "1px solid rgba(239,68,68,0.2)" }}>
                  <i className="fa-solid fa-trash" style={{ fontSize: 10 }} /> Deleted
                </span>
              )}
            </div>
            <p style={{ fontSize: 12, color: "rgba(147,197,253,0.8)", margin: 0 }}>Specialty id: <span style={{ fontWeight: 600, color: "#93c5fd" }}>#{selectedItem.id}</span></p>
          </div>
        </div>
      </div>

      
      <ScrollArea className="flex-1" style={{ background: "#f8fafc" }}>
        <div style={{ padding: "20px 28px 16px" }}>

       
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
            <div style={{ width: 28, height: 28, borderRadius: 8, background: "#eff6ff", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #dbeafe" }}>
              <i className="fa-solid fa-circle-info" style={{ fontSize: 12, color: "#3b82f6" }} />
            </div>
            <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Basic information</span>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 20 }}>
            {[
              { label: "Specialty name", value: selectedItem.name, icon: "fa-stethoscope", color: "#3b82f6" },
              { label: "Specialty code", value: selectedItem.speciality_code || "—", icon: "fa-hashtag", color: "#8b5cf6" },
            ].map((field) => (
              <div key={field.label} style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
                  <div style={{ width: 22, height: 22, borderRadius: 6, background: `${field.color}12`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <i className={`fa-solid ${field.icon}`} style={{ fontSize: 10, color: field.color }} />
                  </div>
                  <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", textTransform: "capitalize", letterSpacing: "0.05em" }}>{field.label}</span>
                </div>
                <span style={{ fontSize: 14, fontWeight: 600, color: "#1e293b" }}>{field.value}</span>
              </div>
            ))}
          </div>

          <div style={{ height: 1, background: "#e2e8f0", margin: "0 0 20px" }} />

          {/* Audit Trail */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
            <div style={{ width: 28, height: 28, borderRadius: 8, background: "#f0fdf4", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #bbf7d0" }}>
              <i className="fa-solid fa-clock-rotate-left" style={{ fontSize: 12, color: "#16a34a" }} />
            </div>
            <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Audit trail</span>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
            <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
              <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8" }}>Created by</span>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 10 }}>
                <div style={{ width: 32, height: 32, borderRadius: "50%", background: "linear-gradient(135deg, #3b82f6, #2563eb)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, color: "#fff", flexShrink: 0 }}>
                  {String(resolveUserName(selectedItem.created_by) ?? "?")[0].toUpperCase()}
                </div>
                <span style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>{resolveUserName(selectedItem.created_by)}</span>
              </div>
            </div>
            <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
              <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8" }}>Created on</span>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}>
                <i className="fa-regular fa-calendar" style={{ fontSize: 14, color: "#3b82f6" }} />
                <span style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>
                  {selectedItem.createdat ? new Date(selectedItem.createdat).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}
                </span>
              </div>
            </div>
            <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
              <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8" }}>Last updated</span>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}>
                <i className="fa-regular fa-clock" style={{ fontSize: 14, color: "#10b981" }} />
                <span style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>
                  {selectedItem.updatedat ? new Date(selectedItem.updatedat).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}
                </span>
              </div>
            </div>
          </div>

       {Array.isArray(selectedItem.updated_by) && selectedItem.updated_by.length > 0 && (
  <div style={{ gridColumn: "1 / -1", background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)", marginTop: 12 }}>
    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 12 }}>
      <i className="fa-solid fa-pen-to-square" style={{ fontSize: 11, color: "#10b981" }} />
      <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.02em" }}>Update History</span>
      <span style={{ fontSize: 10, padding: "1px 8px", borderRadius: 9999, background: "#f0fdf4", color: "#15803d", fontWeight: 600, border: "1px solid #bbf7d0", marginLeft: "auto" }}>
        {selectedItem.updated_by.length} update{selectedItem.updated_by.length !== 1 ? "s" : ""}
      </span>
    </div>
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {(selectedItem.updated_by as { id?: number; at?: string }[]).slice().reverse().slice(0, showAllUpdates ? undefined : 4).map((entry, i) => (
        <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", borderRadius: 8, background: i === 0 ? "#f0fdf4" : "#f8fafc", border: `1px solid ${i === 0 ? "#bbf7d0" : "#e2e8f0"}` }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ width: 28, height: 28, borderRadius: "50%", background: i === 0 ? "linear-gradient(135deg, #10b981, #059669)" : "linear-gradient(135deg, #94a3b8, #64748b)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: "#fff", flexShrink: 0 }}>
              {String(resolveUserName(entry.id) ?? "?")[0].toUpperCase()}
            </div>
            <span style={{ fontSize: 12, fontWeight: 600, color: "#1e293b" }}>{resolveUserName(entry.id)}</span>
            {i === 0 && <span style={{ fontSize: 10, padding: "1px 8px", borderRadius: 9999, background: "#dcfce7", color: "#15803d", fontWeight: 600, border: "1px solid #bbf7d0" }}>Latest</span>}
          </div>
          <span style={{ fontSize: 11, color: "#64748b", fontWeight: 500 }}>
            {entry.at ? new Date(entry.at).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}
          </span>
        </div>
      ))}
      {selectedItem.updated_by.length > 4 && (
        <div style={{ textAlign: "center", padding: "6px 0" }}>
          <button onClick={() => setShowAllUpdates(v => !v)} style={{ fontSize: 11, color: "#3b82f6", fontWeight: 600, background: "none", border: "none", cursor: "pointer" }}>
            {showAllUpdates ? "Show less ↑" : `+${selectedItem.updated_by.length - 4} more updates ↓`}
          </button>
        </div>
      )}
    </div>
  </div>
)}

          <div style={{ paddingBottom: 8 }} />
        </div>
      </ScrollArea>

      {/* Footer */}
      <div style={{ padding: "14px 28px", borderTop: "1px solid #e2e8f0", background: "#fff", display: "flex", alignItems: "center", justifyContent: "flex-end", flexShrink: 0 }}>
        <Button onClick={() => setSelectedItem(null)} style={{ background: "linear-gradient(135deg, #1e3a8a, #2563eb)", color: "#fff", fontWeight: 600, padding: "8px 28px", borderRadius: 10, boxShadow: "0 2px 8px rgba(37,99,235,0.3)" }}>
          Close Details
        </Button>
      </div>
    </DialogContent>
  </Dialog>
)}

      {/* ─── Add / Edit Dialog ───────────────────────────────────────────────── */}
   {/* ─── Add / Edit Dialog ───────────────────────────────────────────────── */}
<Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
  <DialogContent className="max-w-[600px] max-h-[90vh] flex flex-col p-0 bg-white border-none rounded-2xl" style={{ boxShadow: "0 25px 60px rgba(0,0,0,0.18), 0 0 0 1px rgba(0,0,0,0.04)", overflow: "visible" }}>
    <div style={{ background: "linear-gradient(135deg, #0f172a 0%, #1e3a8a 60%, #2563eb 100%)", padding: "22px 28px", position: "relative", overflow: "hidden", borderRadius: "16px 16px 0 0", flexShrink: 0 }}>
      <div style={{ position: "absolute", top: -40, right: -40, width: 140, height: 140, borderRadius: "50%", background: "rgba(255,255,255,0.04)" }} />
      <div style={{ display: "flex", alignItems: "center", gap: 14, position: "relative", zIndex: 1 }}>
        <div style={{ width: 42, height: 42, borderRadius: 12, background: "rgba(255,255,255,0.12)", backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, color: "#fff", flexShrink: 0, border: "1px solid rgba(255,255,255,0.1)" }}>
          <i className={`fa-solid ${editIndex === null ? "fa-plus" : "fa-pen"}`} />
        </div>
        <div>
          <h2 style={{ fontSize: 18, fontWeight: 700, color: "#fff", margin: 0 }}>{editIndex === null ? "Add Specialty" : "Edit Specialty"}</h2>
          </div>
      </div>
    </div>

    <ScrollArea className="flex-1" style={{ background: "#f8fafc" }}>
      <div style={{ padding: "22px 28px 32px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
          <div style={{ width: 26, height: 26, borderRadius: 7, background: "#eff6ff", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #dbeafe" }}>
            <i className="fa-solid fa-circle-info" style={{ fontSize: 11, color: "#3b82f6" }} />
          </div>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Basic Information</span>
        </div>
        <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "18px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="specialty-name" className="text-xs font-semibold text-slate-500">Specialty Name</Label>
              <Input id="specialty-name" value={name} onChange={handleNameChange} placeholder="e.g. Pediatrics" className={`mt-1.5 ${nameError ? "border-red-400 focus-visible:ring-red-300" : ""}`} />
              {nameError && <p className="text-red-500 text-xs mt-1.5 flex items-center gap-1"><i className="fa-solid fa-circle-exclamation text-[10px]" />{nameError}</p>}
            </div>
            <div>
              <Label htmlFor="specialty-code" className="text-xs font-semibold text-slate-500">
                Specialty Code <span className="text-slate-400 font-normal">(optional)</span>
              </Label>
              <Input id="specialty-code" value={specialtyCode} onChange={handleCodeChange} placeholder="e.g. ped" className={`mt-1.5 ${codeError ? "border-red-400 focus-visible:ring-red-300" : ""}`} />
              {codeError && <p className="text-red-500 text-xs mt-1.5 flex items-center gap-1"><i className="fa-solid fa-circle-exclamation text-[10px]" />{codeError}</p>}
            </div>
          </div>

          <div style={{ marginTop: 16, paddingTop: 14, borderTop: "1px solid #e2e8f0" }}>
            <Label className="text-xs font-semibold text-slate-500">Status</Label>
            <div style={{ display: "flex", gap: 8, marginTop: 8, maxWidth: 260 }}>
              <button type="button" onClick={() => setIsActive(true)} style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "8px 0", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer", border: isActive ? "1px solid #16a34a" : "1px solid #e2e8f0", background: isActive ? "#f0fdf4" : "#fff", color: isActive ? "#15803d" : "#64748b" }}>
                <span style={{ width: 6, height: 6, borderRadius: "50%", background: "currentColor" }} /> Active
              </button>
              <button type="button" onClick={() => setIsActive(false)} style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "8px 0", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer", border: !isActive ? "1px solid #dc2626" : "1px solid #e2e8f0", background: !isActive ? "#fef2f2" : "#fff", color: !isActive ? "#b91c1c" : "#64748b" }}>
                <span style={{ width: 6, height: 6, borderRadius: "50%", background: "currentColor" }} /> Inactive
              </button>
            </div>
          </div>
        </div>
      </div>
    </ScrollArea>

    <div style={{ padding: "16px 28px", borderTop: "1px solid #e2e8f0", background: "#fff", display: "flex", justifyContent: "flex-end", gap: 10, borderRadius: "0 0 16px 16px", flexShrink: 0 }}>
      <Button variant="outline" onClick={() => { clear(); setEditIndex(null); setIsDialogOpen(false); }} style={{ borderRadius: 10, fontWeight: 600 }}>Cancel</Button>
      <Button onClick={() => { if (editIndex === null) addItem(); else updateItem(editIndex); }} style={{ background: "linear-gradient(135deg, #1e3a8a, #2563eb)", color: "#fff", fontWeight: 600, padding: "8px 24px", borderRadius: 10, boxShadow: "0 2px 8px rgba(37,99,235,0.3)" }}>
        <i className={`fa-solid ${editIndex === null ? "fa-plus" : "fa-check"} mr-2`}></i>
        {editIndex === null ? "Add Specialty" : "Update Specialty"}
      </Button>
    </div>
  </DialogContent>
</Dialog>

      {/* ─── Step 8: Force / Hard Delete Dialog ──────────────────────────────── */}
      {forceDialogOpen && (
        <div style={{ position: "fixed", inset: 0, background: "#00000088", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", backdropFilter: "blur(4px)" }}>
          <div style={{ background: "#fff", borderRadius: 16, padding: 32, width: 440, boxShadow: "0 20px 50px rgba(0,0,0,0.3)", border: "1px solid #fecaca" }}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", gap: 12 }}>
              <div style={{ width: 60, height: 60, borderRadius: "50%", background: "#fef2f2", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <i className="fa-solid fa-triangle-exclamation" style={{ fontSize: 28, color: "#dc2626" }}></i>
              </div>

              {/* Hard Delete */}
              {forceAction === "hardDelete" && (
                <>
                  <h3 style={{ fontSize: 18, fontWeight: 700, color: "#1e293b", margin: 0 }}>Permanent Delete</h3>
                  <p style={{ fontSize: 13, color: "#64748b", margin: 0, lineHeight: 1.6 }}>
                    This will <strong style={{ color: "#dc2626" }}>permanently delete</strong> the specialty.<br />This action <strong>cannot be undone</strong>.
                  </p>
                  {forceError && (
                    <div style={{ width: "100%", background: "#fef9c3", border: "1px solid #fde047", borderRadius: 10, padding: "12px 16px", textAlign: "left" }}>
                      <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                        <i className="fa-solid fa-circle-info" style={{ color: "#ca8a04", marginTop: 2, flexShrink: 0 }}></i>
                        <p style={{ fontSize: 13, color: "#713f12", margin: 0, lineHeight: 1.6 }}>{forceError}</p>
                      </div>
                    </div>
                  )}
                <div style={{ display: "flex", gap: 10, width: "100%", marginTop: 4 }}>
  <button
    onClick={() => { setForceDialogOpen(false); setForceId(null); setForceError(""); setForceAction(null); }}
    style={{ flex: 1, background: "#f1f5f9", color: "#475569", border: "1px solid #e2e8f0", borderRadius: 9, padding: 12, cursor: "pointer", fontWeight: 600, fontSize: 13 }}>
    Cancel
  </button>
  <button
    onClick={() => forceId !== null && permanentDeleteItem(forceId, true)}
    style={{ flex: 1, background: "#7f1d1d", color: "#fff", border: "none", borderRadius: 9, padding: 12, cursor: "pointer", fontWeight: 700, fontSize: 13 }}>
    <i className="fa-solid fa-bolt mr-2"></i>Force Delete Anyway
  </button>
</div>
                </>
              )}

              {/* Soft Delete / Status */}
              {(forceAction === "softDelete" || forceAction === "status") && (
                <>
                  <h3 style={{ fontSize: 18, fontWeight: 700, color: "#1e293b", margin: 0 }}>
                    {forceAction === "status" ? "Status Change Blocked" : "Specialty Is In Use"}
                  </h3>
                  <div style={{ width: "100%", background: "#fef9c3", border: "1px solid #fde047", borderRadius: 10, padding: "12px 16px", textAlign: "left" }}>
                    <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                      <i className="fa-solid fa-circle-info" style={{ color: "#ca8a04", marginTop: 2, flexShrink: 0 }}></i>
                      <p style={{ fontSize: 13, color: "#713f12", margin: 0, lineHeight: 1.6 }}>{forceError}</p>
                    </div>
                  </div>
                  <p style={{ fontSize: 13, color: "#64748b", margin: 0 }}>
                    Do you want to <strong style={{ color: "#dc2626" }}>force {forceAction === "status" ? "update status" : "delete"}</strong> anyway?
                  </p>
                  <div style={{ display: "flex", gap: 10, width: "100%", marginTop: 4 }}>
                    <button onClick={() => { setForceDialogOpen(false); setForceId(null); setForceError(""); setForceAction(null); }}
                      style={{ flex: 1, background: "#f1f5f9", color: "#475569", border: "1px solid #e2e8f0", borderRadius: 9, padding: 12, cursor: "pointer", fontWeight: 600, fontSize: 13 }}>Cancel</button>
                    <button onClick={() => {
                        if (!forceId) return;
                        if (forceAction === "softDelete") forceSoftDelete(forceId);
                        else if (forceAction === "status") {
                          const item = items.result.find(i => i.id === forceId);
                          if (item) toggleStatus(item, true);
                        }
                      }}
                      style={{ flex: 1, background: "#dc2626", color: "#fff", border: "none", borderRadius: 9, padding: 12, cursor: "pointer", fontWeight: 700, fontSize: 13 }}>
                      <i className="fa-solid fa-bolt mr-2"></i>Force {forceAction === "status" ? "Update" : "Delete"}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <Toaster />
    </div>
  );
};

export default Specialty;